import { COUNTRY_CODE_TO_NAME } from "../../components/MarketOverviewPage/data/constants";
import { OneFormulaData, CoreBrandData, ApiCoreBrandsData, NameCompletionRowData } from "../../components/SettingsPage/types";

export const formatDateToMMDDYYYY = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) {
    return '';
  }
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
};

// Define interfaces for the API data structure
interface ApiWeights {
  market_penetration?: number;
  market_penetration_branded?: number;
  core_availability?: number;
  combo_presence?: number;
  fixed_combo_presence?: number;
  option_combo_presence?: number;
  name_accuracy?: number;
  image_presence?: number;
}

interface ApiMarketData {
  weights?: ApiWeights;
  core_brands?: { name: string; content: any[] }[];
  competitors?: { company: string; brand: string }[];
}

interface ApiFormula {
  [marketKey: string]: ApiMarketData;
}

interface ApiData {
  formula?: ApiFormula;
  created_at?: string;
  created_by?: string;
}

interface TransformedApiData {
    countries: {
        country: string;
        weights: {
            market_penetration: number;
            market_penetration_branded: number;
            core_availability: number;
            combo_presence: number;
            fixed_combo_presence: number;
            option_combo_presence: number;
            name_accuracy: number;
            image_presence: number;
        };
        core_brands: { name: string; content: any[] }[];
        competitors: { company: string; brand: string }[];
    }[];
    comment: string;
}

// Inverse of COUNTRY_CODE_TO_NAME
export const createNameToCodeMap = (): Record<string, string> => {
  const nameToCode: Record<string, string> = {};
  Object.entries(COUNTRY_CODE_TO_NAME).forEach(([code, name]) => {
    nameToCode[name] = code;
  });
  return nameToCode;
};

export const COUNTRY_NAME_TO_CODE = createNameToCodeMap();

// Turn the edited table rows into the API payload
export const transformDataForApi = (oneFormulaData: OneFormulaData[], coreBrandsData: CoreBrandData[]): TransformedApiData => {
  console.log('[transformDataForApi] Input data:', {
    oneFormulaData,
    coreBrandsData,
    COUNTRY_NAME_TO_CODE
  });

  const countries: TransformedApiData['countries'] = [];
  const processedMarkets = new Set();

  // PET Formula rows come first, and processedMarkets records them so the
  // Core Brands pass below only covers markets this pass did not emit
  oneFormulaData.forEach((petItem) => {
    const countryCode = COUNTRY_NAME_TO_CODE[petItem.market];
    console.log(`[transformDataForApi] Processing PET Formula market: ${petItem.market} → countryCode: ${countryCode}`);

    if (!countryCode) {
      // "Default Value" is a virtual row — safe to skip
      if (petItem.market === "Default Value") return;
      // Unknown market would be silently dropped, causing data loss on save
      throw new Error(`Cannot save: unknown market "${petItem.market}" has no country code mapping. This market would be deleted from the config.`);
    }

    processedMarkets.add(petItem.market);

    const coreBrandItem = coreBrandsData.find(cb => cb.market === petItem.market);

    // The table edits percentages; the API stores fractions
    const weights = {
      market_penetration: (petItem.marketPenetration || 0) / 100,
      market_penetration_branded: (petItem.marketPenetrationOfClient || 0) / 100,
      core_availability: (petItem.avgCoreSkuClient || 0) / 100,
      combo_presence: (petItem.comboPresence || 0) / 100,
      fixed_combo_presence: (petItem.fixedComboPresence || 0) / 100,
      option_combo_presence: (petItem.optionComboPresence || 0) / 100,
      name_accuracy: (petItem.nameAccuracy || 0) / 100,
      image_presence: (petItem.avgImagePresence || 0) / 100
    };

    const core_brands: { name: string; content: any[] }[] = [];
    if (coreBrandItem) {
      for (let i = 1; i <= 8; i++) {
        const customCoreBrandStrData = (coreBrandItem as any)[`coreBrand${i}`];
        if (customCoreBrandStrData && customCoreBrandStrData !== "-") {
          try {
            const parsedCoreBrandItem = JSON.parse(customCoreBrandStrData);
            core_brands.push({
              name: parsedCoreBrandItem.brandData.name || parsedCoreBrandItem.displayName || `core_brand_${i}`,
              content: parsedCoreBrandItem.brandData.content || [],
            });

          } catch (error) {
            // A cell that is not JSON is dropped from the payload
            console.warn(`[transformDataForApi] Could not parse brand data for ${`coreBrand${i}`}:`, error);
          }
        }
      }
    }

    const competitors: { company: string; brand: string | null }[] = [];
    if (coreBrandItem && coreBrandItem.competitor && coreBrandItem.competitor !== "-") {
      try {
        const parsed = JSON.parse(coreBrandItem.competitor);
        if (parsed.competitors && Array.isArray(parsed.competitors)) {
          parsed.competitors.forEach((comp: any) => {
            competitors.push({
              company: comp.company || comp.competitor || '',
              brand: comp.brand || null,
            });
          });
        }
      } catch {
        // Legacy fallback: semicolon-separated plain string
        coreBrandItem.competitor.split(';').map((c: string) => c.trim()).filter(Boolean).forEach((comp: string) => {
          competitors.push({ company: comp, brand: null });
        });
      }
    }

    const countryData = {
      country: countryCode,
      weights,
      core_brands,
      competitors
    };

    console.log(`[transformDataForApi] Adding country data:`, countryData);
    countries.push(countryData);
  });

  // Markets that carry Core Brands but no PET Formula row
  coreBrandsData.forEach((coreBrandItem) => {
    if (!processedMarkets.has(coreBrandItem.market)) {
      const countryCode = COUNTRY_NAME_TO_CODE[coreBrandItem.market];
      console.log(`[transformDataForApi] Processing Core Brands only market: ${coreBrandItem.market} → countryCode: ${countryCode}`);

      if (!countryCode) {
        throw new Error(`Cannot save: unknown market "${coreBrandItem.market}" has no country code mapping. This market would be deleted from the config.`);
      }

      const weights = {
        market_penetration: 0,
        market_penetration_branded: 0,
        core_availability: 0,
        combo_presence: 0,
        fixed_combo_presence: 0,
        option_combo_presence: 0,
        name_accuracy: 0,
        image_presence: 0
      };

      const core_brands: { name: string; content: any[] }[] = [];
      for (let i = 1; i <= 8; i++) {
        const brandData = (coreBrandItem as any)[`coreBrand${i}`];
        if (brandData && brandData !== "-") {
          try {
            const parsedData = JSON.parse(brandData);
            core_brands.push({
              name: parsedData.brandData.name || parsedData.displayName || `core_brand_${i}`,
              content: parsedData.brandData.content || [],
            });
          } catch (error) {
            console.warn(`[transformDataForApi] Could not parse brand data for ${`coreBrand${i}`}:`, error);
          }
        }
      }

      const competitors: { company: string; brand: string | null }[] = [];
      if (coreBrandItem.competitor && coreBrandItem.competitor !== "-") {
        try {
          const parsed = JSON.parse(coreBrandItem.competitor);
          if (parsed.competitors && Array.isArray(parsed.competitors)) {
            parsed.competitors.forEach((comp: any) => {
              competitors.push({
                company: comp.company || comp.competitor || '',
                brand: comp.brand || null,
              });
            });
          }
        } catch {
          // Legacy fallback: semicolon-separated plain string
          coreBrandItem.competitor.split(';').map((c: string) => c.trim()).filter(Boolean).forEach((comp: string) => {
            competitors.push({ company: comp, brand: null });
          });
        }
      }

      const countryData = {
        country: countryCode,
        weights,
        core_brands,
        competitors
      };

      console.log(`[transformDataForApi] Adding Core Brands only country data:`, countryData);
      countries.push(countryData);
    }
  });

  const result: TransformedApiData = {
    countries,
    comment: ""
  };

  console.log('[transformDataForApi] Final result:', result);
  return result;
};

export const parseApiDataToOneFormula = (apiData: ApiData): OneFormulaData[] => {
  if (!apiData?.formula) {
    console.warn("[SettingsPage] No formula data found in API response");
    return [];
  }

  // Virtual row: the API returns no "Default Value" market
  const defaultValueRow: OneFormulaData = {
    key: "default-value",
    market: "Default Value",
    marketPenetration: 10,
    marketPenetrationOfClient: 20,
    avgCoreSkuClient: 10,
    comboPresence: 20,
    fixedComboPresence: 10,
    optionComboPresence: 10,
    nameAccuracy: 10,
    avgImagePresence: 10,
    editBy: apiData.created_by || "Unknown",
    lastEditTime: apiData.created_at ? formatDateToMMDDYYYY(apiData.created_at) : undefined,
  };

  const marketDataRows: OneFormulaData[] = Object.entries(apiData.formula).map(([marketKey, marketData]) => {
    const weights = marketData.weights || {};

    return {
      key: `api-${marketKey}`,
      market: (COUNTRY_CODE_TO_NAME as any)[marketKey] || marketKey,
      marketPenetration: parseFloat(((weights.market_penetration || 0) * 100).toFixed(2)),
      marketPenetrationOfClient: parseFloat(((weights.market_penetration_branded || 0) * 100).toFixed(2)),
      avgCoreSkuClient: parseFloat(((weights.core_availability || 0) * 100).toFixed(2)),
      comboPresence: parseFloat(((weights.combo_presence || 0) * 100).toFixed(2)),
      fixedComboPresence: parseFloat(((weights.fixed_combo_presence || 0) * 100).toFixed(2)),
      optionComboPresence: parseFloat(((weights.option_combo_presence || 0) * 100).toFixed(2)),
      nameAccuracy: parseFloat(((weights.name_accuracy || 0) * 100).toFixed(2)),
      avgImagePresence: parseFloat(((weights.image_presence || 0) * 100).toFixed(2)),
      editBy: apiData.created_by || "Unknown",
      lastEditTime: apiData.created_at ? formatDateToMMDDYYYY(apiData.created_at) : undefined,
    };
  });

  const sortedMarketDataRows = marketDataRows.sort((a, b) => a.market.localeCompare(b.market));

  // Default Value stays pinned above the alphabetical markets
  return [defaultValueRow, ...sortedMarketDataRows];
};

export const parseApiDataToCoreBrands = (apiData: ApiData): CoreBrandData[] => {
  if (!apiData?.formula) {
    console.warn("[SettingsPage] No formula data found in API response");
    return [];
  }

  const coreBrandsData = Object.entries(apiData.formula).map(([marketKey, marketData]) => {
    const coreBrands = marketData.core_brands || [];
    const competitors = marketData.competitors || [];

    // The table always renders 8 core brand columns, so pad empty slots with "-"
    const coreBrandColumns: { [key: string]: string } = {};
    for (let i = 1; i <= 8; i++) {
      const brandData = coreBrands[i - 1];
      if (brandData) {
        // The API returns { name, content }; the cell stores the wider
        // { displayName, brandData } shape that transformDataForApi parses back
        const fullBrandData = {
          displayName: brandData.name || "-",
          brandData: {
            name: brandData.name || "-",
            content: brandData.content || []
          }
        };
        coreBrandColumns[`coreBrand${i}`] = JSON.stringify(fullBrandData);
      } else {
        coreBrandColumns[`coreBrand${i}`] = "-";
      }
    }

    return {
      key: `api-${marketKey}`,
      market: (COUNTRY_CODE_TO_NAME as any)[marketKey] || marketKey,
      ...coreBrandColumns,
      competitor: competitors.length > 0
        ? JSON.stringify({ competitors: competitors.map((comp: any) => ({ company: comp.company || null, brand: comp.brand || null })) })
        : "-",
    } as CoreBrandData;
  });

  return coreBrandsData.sort((a, b) => a.market.localeCompare(b.market));
};

export const parseApiDataToNameCompletion = (apiData: any): NameCompletionRowData[] => {
  if (!apiData) return [];
  const NC_KEYS = [
    "subbrand_restaurant", "volume_restaurant", "packaging_type_restaurant", "packaging_size_restaurant",
    "subbrand_grocery", "volume_grocery", "packaging_type_grocery", "packaging_size_grocery",
  ] as const;
  const buildRow = (key: string, market: string, data: any): NameCompletionRowData => {
    const weights: any = {};
    NC_KEYS.forEach((k) => { weights[k] = typeof data[k] === "number" ? data[k] : 0; });
    return {
      key,
      market,
      ...weights,
      is_default: data.is_default ?? true,
      editBy: data.updated_by ?? undefined,
      updated_at: data.updated_at ?? undefined,
    };
  };
  const defaultRow = buildRow("default-value", "Default Value", apiData.default_weights ?? {});
  const countryRows = Object.entries(apiData.countries ?? {})
    .map(([code, data]) => buildRow(code, (COUNTRY_CODE_TO_NAME as any)[code] || code, data as any))
    .sort((a, b) => a.market.localeCompare(b.market));
  return [defaultRow, ...countryRows];
};